import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { cloneDeep } from 'lodash-es';
import { combineLatest, forkJoin, Observable, of, tap } from 'rxjs';
import { catchError, map, mergeMap, take } from 'rxjs/operators';
import { UpdateOutcomeAssessment$Params } from '../../api/core/fn/outcome-assessments/update-outcome-assessment';
import {
  ApiCreateOutcomeAssessmentRequest,
  ApiOutcomeAssessmentResponse,
  ApiOutcomeAssessmentValueRequest,
  ModelsImpactType,
} from '../../api/core/models';
import { CostResponse } from '../../api/core/models/api/cost-response';
import { CreateActionRequest } from '../../api/core/models/api/create-action-request';
import { ImageResponse } from '../../api/core/models/api/image-response';
import { LinkingTextResponse } from '../../api/core/models/api/linking-text-response';
import { UpdateActionCostRequest } from '../../api/core/models/api/update-action-cost-request';
import { WatchlistResponse } from '../../api/core/models/api/watchlist-response';
import { ActionsService, OutcomeAssessmentsService, WatchlistService } from '../../api/core/services';
import { EMPTY_GUID } from '../../constants/empty-guid';
import {
  conditionallyPrefixImagesWithFilesUrl,
  conditionallyRemoveFilesUrlPrefixOnImages,
} from '../../functions/utilities/img-url-utility';
import { ClientAction } from '../../model/client-model/client-action';
import { ClientNode } from '../../model/client-model/client-node';
import { ClientTarget } from '../../model/client-model/client-target';
import { ActionProperties } from '../../model/client-model/properties/action.properties';
import { Language } from '../../model/enums/language';
import { AppConfigService } from '../../services/app-config.service';
import { ModelV2Service } from '../../services/backend-service/model-v2.service';
import { ErrorType } from '../../services/error-handler.service';
import { TransitionElementsStore } from '../transition-elements/transition-elements.store';
import { TransitionStoriesStore } from '../transition-stories/transition-stories.store';
import { actionsEffectsActions, actionsFacadeActions, outcomeAssessmentsActions } from './actions.actions';
import { selectActionsList, selectActionsLoading, selectOutcomeAssessmentsForAction } from './actions.selectors';
import { ActionsState, OutcomeAssessmentState } from './actions.types';

@Injectable({ providedIn: 'root' })
export class ActionsFacadeService {
  private _actionsService = inject(ActionsService);
  private _config = inject(AppConfigService);
  private _modelV2Service = inject(ModelV2Service);
  private _outcomeAssessmentsService = inject(OutcomeAssessmentsService);
  private _store = inject(Store);
  private _transitionElementsStore = inject(TransitionElementsStore);
  private _transitionStoriesStore = inject(TransitionStoriesStore);
  private _watchlistService = inject(WatchlistService);

  get actionsList$(): Observable<ActionsState['actionsList'] | null> {
    return combineLatest([this._store.select(selectActionsList), this._store.select(selectActionsLoading)]).pipe(
      tap(([actions, isLoading]) => {
        if (!actions && !isLoading) {
          this._store.dispatch(actionsFacadeActions.loadActions());
        }
        return actions;
      }),
      map(([actions]) => actions)
    );
  }

  addAction(
    boardId: string,
    boardLanguage: string,
    actionStatusId: string,
    actionTypeId: string,
    title: string,
    description?: string,
    interventionDefinitionId?: string,
    transitionElementIds?: string[]
  ): Observable<ClientAction | false> {
    return this._createMigratedAction(
      boardId,
      boardLanguage,
      actionStatusId,
      actionTypeId,
      title,
      description,
      interventionDefinitionId,
      transitionElementIds
    ).pipe(
      catchError((error) => {
        error.errorType = ErrorType.INTERVENTION_CREATE;
        this._store.dispatch(actionsEffectsActions.createActionFailed({ error }));
        throw error;
      })
    );
  }

  addOrPatchOutcomeAssessment(
    boardId: string,
    boardLanguage: string,
    actionId: string,
    transitionElementId: string,
    update: { description?: string; impactType?: ModelsImpactType; values?: Array<ApiOutcomeAssessmentValueRequest> }
  ): Observable<OutcomeAssessmentState> {
    return this._store.select(selectOutcomeAssessmentsForAction({ actionId })).pipe(
      take(1),
      mergeMap((assessments) => {
        const target = assessments?.find((a) => a.transitionElementId === transitionElementId);
        if (target) {
          return this._patchOutcomeAssessment(boardId, target, boardLanguage, update, actionId, transitionElementId);
        }
        if (!update.impactType) {
          throw new Error('No target outcome assessment found and no impact type provided for creation.');
        }
        return this._addOutcomeAssessment(boardId, boardLanguage, actionId, transitionElementId, {
          description: update.description,
          impactType: update.impactType!,
          values: update.values ?? [],
        });
      })
    );
  }

  deleteAction(boardId: string, action: ClientAction): Observable<ClientAction | undefined> {
    return this._actionsService.deleteAction({ boardId, id: action.id }).pipe(
      map(() => {
        this._store.dispatch(actionsEffectsActions.deleteActionSuccess({ actionId: action.id }));
        return action;
      }),
      catchError((error) => {
        this._store.dispatch(actionsEffectsActions.deleteActionFailed({ error }));
        return of(undefined);
      })
    );
  }

  deleteOutcomeAssessment(
    boardId: string,
    actionId: string,
    transitionElementId: string
  ): Observable<boolean | undefined> {
    return this._store.select(selectOutcomeAssessmentsForAction({ actionId })).pipe(
      take(1),
      mergeMap((assessments) => {
        const target = assessments?.find((a) => a.transitionElementId === transitionElementId);
        if (!target) {
          return of(undefined);
        }
        return this._outcomeAssessmentsService
          .deleteOutcomeAssessment({
            boardId,
            id: target.id,
          })
          .pipe(
            map(() => {
              this._store.dispatch(
                outcomeAssessmentsActions.deleteOutcomeAssessmentSuccess({
                  actionId,
                  outcomeAssessmentId: target.id,
                })
              );
              return true;
            })
          );
      }),
      catchError((error) => {
        console.error('Error deleting outcome assessment:', error);
        return of(false);
      })
    );
  }

  duplicateIntervention({
    boardCurrency,
    boardId,
    boardLanguage,
    connectedTransitionElements,
    connectedLegacyCustomElements,
    intervention,
  }: {
    boardCurrency: string;
    boardId: string;
    boardLanguage: Language;
    connectedTransitionElements: string[];
    connectedLegacyCustomElements: string[];
    intervention: ClientAction;
  }): Observable<{
    duplicatedIntervention: ClientAction;
    updatedNodes: ClientNode[];
    updatedTransitionElements: ClientTarget[];
  }> {
    const duplicateBaseIntervention$ = () => {
      intervention.transitionElementIds = connectedTransitionElements;
      intervention.legacyCustomElementIds = connectedLegacyCustomElements;
      return this._actionsService
        .createAction({
          boardId,
          body: <CreateActionRequest>(
            this._modelV2Service.toCreateOrUpdateActionRequestFromClientAction(intervention, boardLanguage)
          ),
        })
        .pipe(
          catchError((error) => {
            error.errorType = ErrorType.INTERVENTION_CREATE;
            this._store.dispatch(actionsEffectsActions.createActionFailed({ error }));
            throw error;
          }),
          mergeMap(({ id }) => this._actionsService.getAction({ boardId, id, 'Accept-Language': boardLanguage })),
          map((ApiDuplicatedIntervention) =>
            this._modelV2Service.toClientActionFromActionResponse(ApiDuplicatedIntervention)
          )
        );
    };

    return duplicateBaseIntervention$().pipe(
      mergeMap((duplicatedIntervention) => {
        const requests: {
          duplicatedIntervention: Observable<ClientAction>;
          updatedTransitionElements: Observable<ClientTarget[]>;
          updatedNodes: Observable<ClientNode[]>;
          costDistribution?: Observable<CostResponse>;
          images?: Observable<ImageResponse[]>;
          linkingTexts?: Observable<LinkingTextResponse[]>;
          watchlist?: Observable<WatchlistResponse>;
        } = {
          duplicatedIntervention: of(duplicatedIntervention),
          updatedTransitionElements: of([]),
          updatedNodes: of([]),
        };

        if (!!intervention.actionProperties?.costDistribution) {
          const costDistribution$ = this._actionsService.createCostForAction({
            boardId,
            actionId: duplicatedIntervention.id,
            body: {
              value: intervention.actionProperties.costDistribution.totalCost,
              currency: boardCurrency,
              description: this._modelV2Service.toLocalisedString(
                intervention.actionProperties.costDistribution.totalCostInformation ?? '',
                boardLanguage
              ),
              costProportions: intervention.actionProperties.costDistribution?.stakeholders
                ?.map((stakeholder) => ({
                  stakeholderId: stakeholder.id,
                  proportion: stakeholder.proportion / 100,
                }))
                .filter((s) => s.proportion > 0),
            },
          });
          requests.costDistribution = costDistribution$;
        }

        if (!!intervention.actionProperties?.images?.length) {
          const images$ = forkJoin(
            intervention.actionProperties?.images.map((img) => {
              return this._actionsService.createImage({
                actionId: duplicatedIntervention.id,
                boardId,
                body: {
                  imageUrl: conditionallyRemoveFilesUrlPrefixOnImages(img.imageUrl, this._config.baseUrlFiles),
                  credit: this._modelV2Service.toLocalisedString(img.credit!, boardLanguage),
                  description: this._modelV2Service.toLocalisedString(img.description!, boardLanguage),
                },
              });
            })
          );
          requests.images = images$;
        }

        if (!!intervention.actionProperties?.linkingTexts?.length) {
          const linkingTexts$ = forkJoin(
            intervention.actionProperties?.linkingTexts.map((lt) => {
              return this._actionsService.createLinkingText({
                actionId: duplicatedIntervention.id,
                boardId,
                body: {
                  title: this._modelV2Service.toLocalisedString(lt.title, boardLanguage),
                  linkUrl: this._modelV2Service.toLocalisedString(lt.linkUrl!, boardLanguage),
                },
              });
            })
          );
          requests.linkingTexts = linkingTexts$;
        }

        if (
          !!intervention.actionProperties?.comment ||
          !!intervention.actionProperties?.date ||
          !!intervention.actionProperties?.responsibleMember?.length
        ) {
          const watchlist$ = this._watchlistService.createWatchlist({
            boardId,
            body: {
              date: intervention.actionProperties?.date?.toISOString() as string,
              userIds: <string[]>intervention.actionProperties?.responsibleMember,
              comment: intervention.actionProperties?.comment,
              owner: 'actions',
              ownerId: duplicatedIntervention.id,
            },
          });
          requests.watchlist = watchlist$;
        }

        return forkJoin(requests);
      }),
      map(
        ({
          duplicatedIntervention,
          updatedNodes,
          updatedTransitionElements,
          costDistribution,
          images,
          linkingTexts,
          watchlist,
        }) => {
          if (costDistribution) {
            duplicatedIntervention.actionProperties.costDistribution = {
              ...intervention.actionProperties.costDistribution!,
              id: costDistribution.id,
            };
          }

          if (images) {
            duplicatedIntervention.actionProperties.images = images.map((image, index) => ({
              ...image,
              credit: intervention.actionProperties.images![index].credit ?? '',
              description: intervention.actionProperties.images![index].description ?? '',
              imageUrl: conditionallyPrefixImagesWithFilesUrl(image.imageUrl, this._config.baseUrlFiles),
            }));
            duplicatedIntervention.actionProperties.imgURL = JSON.stringify(
              images.map((image, index) => ({
                credit: intervention.actionProperties.images![index].description ?? null,
                description: intervention.actionProperties.images![index].credit ?? null,
                id: image?.id ?? null,
                url: conditionallyPrefixImagesWithFilesUrl(image.imageUrl, this._config.baseUrlFiles),
              }))
            );
          }

          if (linkingTexts) {
            duplicatedIntervention.actionProperties.linkingTexts = linkingTexts;
          }

          if (watchlist) {
            duplicatedIntervention.actionProperties.watchlist = watchlist;
          }

          return { duplicatedIntervention, updatedNodes, updatedTransitionElements };
        }
      ),
      tap(({ duplicatedIntervention }) => {
        this._transitionStoriesStore.patchElementsWithActionConnections({
          elementIds: [...connectedTransitionElements, ...connectedLegacyCustomElements],
          actionId: duplicatedIntervention.id,
        });
        this._transitionElementsStore.updateTransitionElementActionIds({
          transitionElementIds: connectedTransitionElements,
          actionId: duplicatedIntervention.id,
        });
        this._store.dispatch(actionsEffectsActions.createActionSuccess({ action: duplicatedIntervention }));
      })
    );
  }

  getOutcomeAssessments(
    boardId: string,
    boardLanguage: string,
    actionId: string
  ): Observable<OutcomeAssessmentState[]> {
    return this._store.select(selectOutcomeAssessmentsForAction({ actionId })).pipe(
      mergeMap((assessments) => {
        if (assessments) {
          return of(assessments);
        }
        return this._outcomeAssessmentsService
          .listOutcomeAssessmentsByActionId({ boardId, id: actionId, 'Accept-Language': boardLanguage })
          .pipe(
            map((response) =>
              (response.outcomeAssessments ?? []).map((item) => this._mapOutcomeAssessmentResponseToStoreObject(item))
            ),
            tap((items) => {
              this._store.dispatch(
                outcomeAssessmentsActions.loadOutcomeAssessmentsSuccess({ actionId, outcomeAssessments: items })
              );
            }),
            catchError((error) => {
              console.error('Error fetching outcome assessments:', error);
              return of([]);
            })
          );
      })
    );
  }

  /**
   * This method goes through the action, which for non migrated action boards contains the complete ClientAction,
   * but for migrated action boards only contains the changed properties from the form. It then patches/creates/deletes
   * the necessary action, images, linking texts, watchlist and cost depending on the changes.
   *
   * **Note:** Exception for linking texts and images, even if the action is migrated the complete lists of linking texts
   * and images will be sent in the action, so we can figure out which to patch/delete/create.
   *
   * @param boardId
   * @param boardLanguage
   * @param boardCurrency
   * @param oldAction {ClientAction} contains the complete action, from the latest saved state
   * @param action contains the changed properties from the form, not the complete action (when migrated actions)
   */
  patchAction(
    boardId: string,
    boardLanguage: string,
    boardCurrency: string,
    oldAction: ClientAction,
    action: Partial<ClientAction> & { id: string; actionProperties: ActionProperties }
  ): Observable<false | Partial<ClientAction>> {
    const actionToPatch = cloneDeep(action);
    let requests: Observable<any>[] = [];
    // create/patch/delete any of the images
    const imageRequests = this._imageRequests(actionToPatch, oldAction, boardId, boardLanguage);
    requests = requests.concat(imageRequests);

    // create/patch/delete any of the linking texts
    const linkingTextRequests = this._linkingTextRequests(actionToPatch, oldAction, boardId, boardLanguage);
    requests = requests.concat(linkingTextRequests);

    // create/patch/delete the watchlist
    const watchlistReq = this._watchlistRequest(actionToPatch, oldAction, boardId);
    if (watchlistReq !== null) {
      requests.push(watchlistReq);
    }

    // create/patch/delete cost
    const costRequest = this._costRequest(actionToPatch, oldAction, boardId, boardLanguage, boardCurrency);
    if (costRequest !== null) {
      requests.push(costRequest);
    }

    // checking if we need to patch the action
    if (this._modelV2Service.shouldPatchAction(actionToPatch)) {
      requests.push(
        this._actionsService.updateAction({
          id: actionToPatch.id,
          boardId,
          body: this._modelV2Service.toCreateOrUpdateActionRequestFromClientAction(actionToPatch, boardLanguage),
        })
      );
    }
    if (requests.length === 0) {
      console.error('No requests sent to backend, patchAction was called without anything to patch.', actionToPatch);
      return of(<false>false);
    }
    return forkJoin(requests).pipe(
      map(() => actionToPatch),
      tap(() => {
        this._store.dispatch(actionsEffectsActions.savedAction({ savedAction: cloneDeep(actionToPatch) }));
      })
    );
  }

  patchActionConnections(
    actionId: string,
    boardId: string,
    transitionElementIds: string[],
    legacyCustomElementIds: string[]
  ): Observable<string> {
    const body = <any>{
      transitionElementIds,
      legacyCustomElementIds,
    };
    return this._actionsService
      .updateAction({
        id: actionId,
        boardId,
        body,
      })
      .pipe(
        tap(() => {
          this._store.dispatch(actionsEffectsActions.savedAction({ savedAction: { id: actionId, ...body } }));
          this._store.dispatch(
            outcomeAssessmentsActions.patchedActionConnectionsSuccess({
              actionId,
              remainingTransitionElementIds: transitionElementIds,
            })
          );
          this._transitionStoriesStore.patchElementsWithActionConnections({
            elementIds: [...transitionElementIds, ...legacyCustomElementIds],
            actionId,
          });
          this._transitionElementsStore.updateTransitionElementActionIds({ transitionElementIds, actionId });
        }),
        map(() => actionId)
      );
  }

  resetActionsState(): void {
    return this._store.dispatch(actionsEffectsActions.loadedActions({ actionsList: null }));
  }

  resetOutcomeAssessmentsState() {
    return this._store.dispatch(outcomeAssessmentsActions.clearOutcomeAssessments());
  }

  private _addOutcomeAssessment(
    boardId: string,
    boardLanguage: string,
    actionId: string,
    transitionElementId: string,
    body: { description?: string; impactType: ModelsImpactType; values?: Array<ApiOutcomeAssessmentValueRequest> }
  ): Observable<OutcomeAssessmentState> {
    const requestBody: ApiCreateOutcomeAssessmentRequest = {
      ...body,
      description: body.description
        ? this._modelV2Service.toLocalisedString(body.description, boardLanguage)
        : undefined,
      transitionElementId,
      actionId,
    };

    return this._outcomeAssessmentsService
      .createOutcomeAssessment({
        boardId,
        body: requestBody,
      })
      .pipe(
        map((response) => ({
          actionId,
          description: body.description,
          id: response,
          impactType: body.impactType,
          transitionElementId,
          values: body.values,
        })),
        tap((createdAssessment) => {
          this._store.dispatch(
            outcomeAssessmentsActions.createOutcomeAssessmentSuccess({
              actionId,
              createdOutcomeAssessment: createdAssessment,
            })
          );
        })
      );
  }

  private _costRequest(
    action: Partial<ClientAction> & { id: string; actionProperties: ActionProperties },
    oldAction: ClientAction,
    boardId: string,
    boardLanguage: string,
    boardCurrency: string
  ): Observable<any> | null {
    if (!!action.actionProperties?.costDistribution) {
      if (
        oldAction.actionProperties.costDistribution?.id &&
        oldAction.actionProperties.costDistribution?.id !== EMPTY_GUID
      ) {
        const body: UpdateActionCostRequest = {
          value:
            action.actionProperties.costDistribution.totalCost ??
            oldAction.actionProperties.costDistribution?.totalCost,
          description: this._modelV2Service.toLocalisedString(
            (action.actionProperties?.costDistribution?.totalCostInformation ||
            action.actionProperties?.costDistribution?.totalCostInformation === null // being null, means user has deleted the field
              ? action.actionProperties?.costDistribution?.totalCostInformation
              : oldAction.actionProperties.costDistribution?.totalCostInformation) as string,
            boardLanguage
          ),
          stakeholderCostProportions:
            action.actionProperties.costDistribution?.stakeholders
              ?.map((s) => ({
                stakeholderId: s.id,
                proportion: s.proportion / 100,
              }))
              .filter((s) => s.proportion > 0) ??
            oldAction.actionProperties.costDistribution?.stakeholders
              .map((s) => ({
                stakeholderId: s.id,
                proportion: s.proportion / 100,
              }))
              .filter((s) => s.proportion > 0),
        };
        return this._actionsService.updateCostForAction({
          boardId,
          actionId: action.id,
          body,
        });
      } else {
        return this._actionsService
          .createCostForAction({
            boardId,
            actionId: action.id,
            body: {
              value: action.actionProperties.costDistribution.totalCost,
              currency: boardCurrency,
              description: this._modelV2Service.toLocalisedString(
                action.actionProperties.costDistribution.totalCostInformation ?? '',
                boardLanguage
              ),
              costProportions: action.actionProperties.costDistribution?.stakeholders
                ?.map((stakeholder) => ({
                  stakeholderId: stakeholder.id,
                  proportion: stakeholder.proportion / 100,
                }))
                .filter((s) => s.proportion > 0),
            },
          })
          .pipe(
            tap((res) => {
              action.actionProperties.costDistribution!.id = res.id;
            })
          );
      }
    } else if (
      action.actionProperties?.costDistribution?.totalCost === undefined &&
      action.actionProperties?.costDistribution?.totalCostInformation === null &&
      action.actionProperties?.costDistribution?.stakeholders?.length === 0
    ) {
      return this._actionsService.deleteCostByActionId({
        boardId,
        actionId: action.id,
      });
    }
    return null;
  }

  private _createMigratedAction(
    boardId: string,
    boardLanguage: string,
    actionStatusId: string,
    actionTypeId: string,
    title: string,
    description?: string,
    interventionDefinitionId?: string,
    transitionElementIds?: string[]
  ): Observable<ClientAction> {
    return this._actionsService
      .createAction({
        boardId,
        body: {
          public: true,
          title: this._modelV2Service.toLocalisedString(title, boardLanguage),
          ragRating: 'NONE',
          actionStatusId,
          actionTypeId,
          affected: {},
          description: description ? this._modelV2Service.toLocalisedString(description, boardLanguage) : {},
          barriers: {},
          effect: {},
          reference: {},
          contribution: {},
          emissionEnergyInfo: {},
          necessaryStakeholders: {},
          responsibleOrganisation: {},
          interventionDefinitionId,
          transitionElementIds,
        },
      })
      .pipe(
        mergeMap((res) => {
          return this._actionsService
            .getAction({ boardId, id: res.id, 'Accept-Language': boardLanguage })
            .pipe(map((a) => this._modelV2Service.toClientActionFromActionResponse(a)));
        }),
        map((newAction) => {
          this._store.dispatch(actionsEffectsActions.createActionSuccess({ action: newAction }));
          return newAction;
        })
      );
  }

  private _imageRequests(
    action: Partial<ClientAction> & { id: string },
    oldAction: ClientAction,
    boardId: string,
    boardLanguage: string
  ) {
    let requests: Observable<any>[] = [];
    if (action.actionProperties?.images) {
      const oldImages = oldAction.actionProperties.images ?? [];
      action.actionProperties?.images.forEach((img: ImageResponse) => {
        const oi = oldImages.find((i: ImageResponse) => i.id === img.id);
        // if image exists in old array, we need to patch, otherwise create a new one
        if (!!oi) {
          if (oi.credit !== img.credit || oi.description !== img.description || oi.imageUrl !== img.imageUrl) {
            requests.push(
              this._actionsService.updateImage({
                actionId: action.id,
                boardId,
                id: img.id,
                body: {
                  imageUrl: conditionallyRemoveFilesUrlPrefixOnImages(img.imageUrl, this._config.baseUrlFiles),
                  credit: this._modelV2Service.toLocalisedString(img.credit!, boardLanguage),
                  description: this._modelV2Service.toLocalisedString(img.description!, boardLanguage),
                },
              })
            );
          }
        } else {
          requests.push(
            this._actionsService
              .createImage({
                actionId: action.id,
                boardId,
                body: {
                  imageUrl: conditionallyRemoveFilesUrlPrefixOnImages(img.imageUrl, this._config.baseUrlFiles),
                  credit: this._modelV2Service.toLocalisedString(img.credit!, boardLanguage),
                  description: this._modelV2Service.toLocalisedString(img.description!, boardLanguage),
                },
              })
              .pipe(map((res) => (img.id = res.id)))
          );
        }
      });
      // deleting any images that doesn't exist in updated action.
      const imagesToDelete = oldImages.filter(
        (img: ImageResponse) => !action?.actionProperties?.images!.find((i: ImageResponse) => i.id === img.id)
      );
      requests = requests.concat(
        imagesToDelete.map((img: ImageResponse) =>
          this._actionsService.deleteImage({ actionId: action.id, boardId, id: img.id })
        )
      );
    }
    return requests;
  }

  private _linkingTextRequests(
    action: Partial<ClientAction> & { id: string },
    oldAction: ClientAction,
    boardId: string,
    boardLanguage: string
  ) {
    let requests: Observable<any>[] = [];
    if (action.actionProperties?.linkingTexts) {
      const oldLinkingTexts = oldAction.actionProperties.linkingTexts ?? [];
      action.actionProperties?.linkingTexts.forEach((lt) => {
        const ol = oldLinkingTexts.find((l) => l.id === lt.id);
        // if linkingText exists in old array, we need to patch, otherwise create a new one
        if (!!ol) {
          const titleToUse = lt.title ?? ol.title;
          const linkUrlToUse = lt.linkUrl ?? ol.linkUrl;

          if (titleToUse && linkUrlToUse && (ol.title !== lt.title || ol.linkUrl !== lt.linkUrl)) {
            requests.push(
              this._actionsService.updateLinkingText({
                actionId: action.id,
                boardId,
                id: lt.id,
                body: {
                  title: this._modelV2Service.toLocalisedString(titleToUse, boardLanguage),
                  linkUrl: this._modelV2Service.toLocalisedString(linkUrlToUse, boardLanguage),
                },
              })
            );
          }
        } else {
          if (lt.title && lt.linkUrl) {
            requests.push(
              this._actionsService
                .createLinkingText({
                  actionId: action.id,
                  boardId,
                  body: {
                    title: this._modelV2Service.toLocalisedString(lt.title, boardLanguage),
                    linkUrl: this._modelV2Service.toLocalisedString(lt.linkUrl, boardLanguage),
                  },
                })
                .pipe(tap((res) => (lt.id = res.id)))
            );
          }
        }
      });
      // deleting any linkingTexts that doesn't exist in updated action.
      const linkingTextsToDelete = oldLinkingTexts.filter(
        (lt) => !action.actionProperties?.linkingTexts?.find((l) => l?.id === lt.id)
      );
      requests = requests.concat(
        linkingTextsToDelete.map((lt) =>
          this._actionsService.deleteLinkingText({ actionId: action.id, boardId, id: lt.id })
        )
      );
    }
    return requests;
  }

  private _mapOutcomeAssessmentResponseToStoreObject(item: ApiOutcomeAssessmentResponse): OutcomeAssessmentState {
    return {
      id: item.id,
      actionId: item.actionId,
      transitionElementId: item.transitionElementId,
      description: item.description,
      impactType: item.impactType,
      values: item.values?.map((value) => ({
        value: value.value ? parseFloat(value.value) : undefined,
        year: value.year,
      })),
    };
  }

  private _patchOutcomeAssessment(
    boardId: string,
    target: OutcomeAssessmentState,
    boardLanguage: string,
    update: { description?: string; impactType?: ModelsImpactType; values?: Array<ApiOutcomeAssessmentValueRequest> },
    actionId: string,
    transitionElementId: string
  ) {
    const params: UpdateOutcomeAssessment$Params = {
      boardId,
      id: target.id,
      'Accept-Language': boardLanguage,
      body: {
        description:
          update.description !== undefined
            ? this._modelV2Service.toLocalisedString(update.description, boardLanguage)
            : target.description
              ? this._modelV2Service.toLocalisedString(target.description, boardLanguage)
              : undefined,
        impactType: update.impactType ?? target.impactType,
        values: update.values ?? target.values,
      },
    };

    return this._outcomeAssessmentsService.updateOutcomeAssessment(params).pipe(
      map((response) => this._mapOutcomeAssessmentResponseToStoreObject(response)),
      tap((updatedAssessment) => {
        this._store.dispatch(
          outcomeAssessmentsActions.savedOutcomeAssessment({
            actionId,
            transitionElementId,
            savedOutcomeAssessment: {
              impactType: updatedAssessment.impactType,
              description: updatedAssessment.description,
              values: updatedAssessment.values,
            },
          })
        );
      })
    );
  }

  private _watchlistRequest(
    action: Partial<ClientAction> & { id: string },
    oldAction: ClientAction,
    boardId: string
  ): Observable<any> | null {
    function getUserIds() {
      if (action?.actionProperties?.responsibleMember) {
        const { responsibleMember } = action.actionProperties;
        return Array.isArray(responsibleMember) ? responsibleMember : [responsibleMember];
      }
      if (oldAction.actionProperties.responsibleMember) {
        const { responsibleMember } = oldAction.actionProperties;
        return Array.isArray(responsibleMember) ? responsibleMember : [responsibleMember];
      }
      return [];
    }

    if (
      !!action.actionProperties?.comment ||
      !!action.actionProperties?.date ||
      !!action.actionProperties?.responsibleMember?.length
    ) {
      if (oldAction.actionProperties.watchlist?.id) {
        const body = {
          date: <string>(
            (action.actionProperties?.date?.toISOString() ?? oldAction.actionProperties?.date?.toISOString() ?? null)
          ),
          userIds: getUserIds(),
          comment: action.actionProperties?.comment ?? oldAction.actionProperties.comment,
        };
        return this._watchlistService
          .updateWatchlist({
            boardId,
            id: oldAction.actionProperties.watchlist.id, // using the id from oldAction, since action only contains edited fields
            body: {
              // need to check for oldValues, since update only contains edited fields
              ...body,
              owner: 'actions',
              ownerId: action.id,
            },
          })
          .pipe(
            tap(
              () =>
                (action!.actionProperties!.watchlist = {
                  ...action!.actionProperties!.watchlist,
                  ...body,
                } as WatchlistResponse)
            )
          );
      } else {
        return this._watchlistService
          .createWatchlist({
            boardId,
            body: {
              date: (action.actionProperties?.date?.toISOString() as string) ?? null,
              userIds: <string[]>action.actionProperties?.responsibleMember,
              comment: action.actionProperties?.comment,
              owner: 'actions',
              ownerId: action.id,
            },
          })
          .pipe(tap((res) => (action.actionProperties!.watchlist = res)));
      }
    } else if (
      action.actionProperties?.comment === '' &&
      action.actionProperties?.date === null &&
      getUserIds().length === 0 &&
      oldAction.actionProperties.watchlist?.id
    ) {
      return this._watchlistService.deleteWatchlist({ boardId, id: oldAction.actionProperties.watchlist.id });
    }
    return null;
  }
}
